import { LookupSupportedLocales, ResolveLocale } from "@formatjs/intl-localematcher";
import { BigDecimal, Decimal } from "@formatjs/bigdecimal";
//#region packages/ecma262-abstract/OrdinaryHasInstance.js
function IsCallable$1(fn) {
	return typeof fn === "function";
}
/**
* https://tc39.es/ecma262/#sec-ordinaryhasinstance
*/
function OrdinaryHasInstance(C, O, internalSlots) {
	if (!IsCallable$1(C)) return false;
	if (internalSlots?.boundTargetFunction) return O instanceof internalSlots?.boundTargetFunction;
	if (typeof O !== "object") return false;
	let P = C.prototype;
	if (typeof P !== "object") throw new TypeError("OrdinaryHasInstance called on an object with an invalid prototype property.");
	return Object.prototype.isPrototypeOf.call(P, O);
}
//#endregion
//#region packages/ecma262-abstract/ToObject.js
/**
* https://tc39.es/ecma262/#sec-toobject
*/
function ToObject(arg) {
	if (arg == null) throw new TypeError("undefined/null cannot be converted to object");
	return Object(arg);
}
//#endregion
//#region packages/ecma402-abstract/CanonicalizeLocaleList.js
/**
* http://ecma-international.org/ecma-402/7.0/index.html#sec-canonicalizelocalelist
* @param locales
*/
function CanonicalizeLocaleList(locales) {
	return Intl.getCanonicalLocales(locales);
}
//#endregion
//#region packages/ecma402-abstract/DefaultNumberOption.js
/**
* https://tc39.es/ecma402/#sec-defaultnumberoption
* @param val
* @param min
* @param max
* @param fallback
*/
function DefaultNumberOption(inputVal, min, max, fallback) {
	if (inputVal === void 0) return fallback;
	const val = Number(inputVal);
	if (isNaN(val) || val < min || val > max) throw new RangeError(`${val} is outside of range [${min}, ${max}]`);
	return Math.floor(val);
}
//#endregion
//#region packages/ecma402-abstract/GetNumberOption.js
/**
* https://tc39.es/ecma402/#sec-getnumberoption
* @param options
* @param property
* @param min
* @param max
* @param fallback
*/
function GetNumberOption(options, property, minimum, maximum, fallback) {
	const val = options[property];
	return DefaultNumberOption(val, minimum, maximum, fallback);
}
//#endregion
//#region packages/ecma262-abstract/ToString.js
/**
* https://tc39.es/ecma262/#sec-tostring
*/
function ToString(o) {
	if (typeof o === "symbol") throw TypeError("Cannot convert a Symbol value to a string");
	return String(o);
}
//#endregion
//#region packages/ecma402-abstract/GetOption.js
/**
* https://tc39.es/ecma402/#sec-getoption
* @param opts
* @param prop
* @param type
* @param values
* @param fallback
*/
function GetOption(opts, prop, type, values, fallback) {
	if (typeof opts !== "object") throw new TypeError("Options must be an object");
	let value = opts[prop];
	if (value !== void 0) {
		if (type !== "boolean" && type !== "string") throw new TypeError("invalid type");
		if (type === "boolean") value = Boolean(value);
		if (type === "string") value = ToString(value);
		if (values !== void 0 && !values.filter((val) => val == value).length) throw new RangeError(`${value} is not within ${values.join(", ")}`);
		return value;
	}
	return fallback;
}
//#endregion
//#region packages/ecma402-abstract/SupportedLocales.js
/**
* https://tc39.es/ecma402/#sec-supportedlocales
* @param availableLocales
* @param requestedLocales
* @param options
*/
function SupportedLocales(availableLocales, requestedLocales, options) {
	let matcher = "best fit";
	if (options !== void 0) {
		options = ToObject(options);
		matcher = GetOption(options, "localeMatcher", "string", ["lookup", "best fit"], "best fit");
	}
	if (matcher === "best fit") return LookupSupportedLocales(Array.from(availableLocales), requestedLocales);
	return LookupSupportedLocales(Array.from(availableLocales), requestedLocales);
}
//#endregion
//#region node_modules/.aspect_rules_js/@formatjs+fast-memoize@0.0.0/node_modules/@formatjs/fast-memoize/index.js
function memoize(fn, options) {
	const cache = options && options.cache ? options.cache : cacheDefault;
	const serializer = options && options.serializer ? options.serializer : serializerDefault;
	return (options && options.strategy ? options.strategy : strategyDefault)(fn, {
		cache,
		serializer
	});
}
function isPrimitive(value) {
	return value == null || typeof value === "number" || typeof value === "boolean";
}
function monadic(fn, cache, serializer, arg) {
	const cacheKey = isPrimitive(arg) ? arg : serializer(arg);
	let computedValue = cache.get(cacheKey);
	if (typeof computedValue === "undefined") {
		computedValue = fn.call(this, arg);
		cache.set(cacheKey, computedValue);
	}
	return computedValue;
}
function variadic(fn, cache, serializer) {
	const args = Array.prototype.slice.call(arguments, 3);
	const cacheKey = serializer(args);
	let computedValue = cache.get(cacheKey);
	if (typeof computedValue === "undefined") {
		computedValue = fn.apply(this, args);
		cache.set(cacheKey, computedValue);
	}
	return computedValue;
}
function assemble(fn, context, strategy, cache, serialize) {
	return strategy.bind(context, fn, cache, serialize);
}
function strategyDefault(fn, options) {
	const strategy = fn.length === 1 ? monadic : variadic;
	return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}
function strategyVariadic(fn, options) {
	return assemble(fn, this, variadic, options.cache.create(), options.serializer);
}
function strategyMonadic(fn, options) {
	return assemble(fn, this, monadic, options.cache.create(), options.serializer);
}
const serializerDefault = function() {
	return JSON.stringify(arguments);
};
var ObjectWithoutPrototypeCache = class {
	constructor() {
		this.cache = Object.create(null);
	}
	get(key) {
		return this.cache[key];
	}
	set(key, value) {
		this.cache[key] = value;
	}
};
const cacheDefault = { create: function create() {
	return new ObjectWithoutPrototypeCache();
} };
const strategies = {
	variadic: strategyVariadic,
	monadic: strategyMonadic
};
//#endregion
//#region packages/ecma402-abstract/utils.js
function defineProperty(target, name, { value }) {
	Object.defineProperty(target, name, {
		configurable: true,
		enumerable: false,
		writable: true,
		value
	});
}
function ensureIntl() {
	if (typeof Intl === "undefined") Object.defineProperty(globalThis, "Intl", {
		configurable: true,
		enumerable: false,
		writable: true,
		value: {}
	});
	return Intl;
}
function invariant$2(condition, message, Err = Error) {
	if (!condition) throw new Err(message);
}
const createMemoizedNumberFormat = memoize((...args) => new Intl.NumberFormat(...args), { strategy: strategies.variadic });
memoize((...args) => new Intl.PluralRules(...args), { strategy: strategies.variadic });
const createMemoizedListFormat = memoize((...args) => new Intl.ListFormat(...args), { strategy: strategies.variadic });
//#endregion
//#region packages/ecma402-abstract/DurationFormat/GetDurationUnitOptions.js
function GetDurationUnitOptions(unit, options, baseStyle, stylesList, digitalBase, prevStyle, twoDigitHours = false) {
	let style = GetOption(options, unit, "string", stylesList, void 0);
	let displayDefault = "always";
	if (style === void 0) if (baseStyle === "digital") {
		if (unit !== "hours" && unit !== "minutes" && unit !== "seconds") displayDefault = "auto";
		style = digitalBase;
	} else {
		displayDefault = "auto";
		if (prevStyle === "numeric" || prevStyle === "2-digit") style = "numeric";
		else style = baseStyle;
	}
	let display = GetOption(options, `${unit}Display`, "string", ["always", "auto"], displayDefault);
	if (twoDigitHours && unit === "hours" && style === "numeric") style = "2-digit";
	if (prevStyle === "numeric" || prevStyle === "2-digit") {
		if (style !== "numeric" && style !== "2-digit") throw new RangeError("Can't mix numeric and non-numeric styles");
		else if (unit === "minutes" || unit === "seconds") style = "2-digit";
		if (style === "numeric" && (unit === "milliseconds" || unit === "microseconds" || unit === "nanoseconds")) {
			if (display === "always") throw new RangeError("Can't display milliseconds, microseconds, or nanoseconds in numeric format");
			style = "fractional";
		}
	}
	return {
		style,
		display
	};
}
//#endregion
//#region packages/intl-durationformat/constants.ts
const TABLE_2 = [
	{
		valueField: "years",
		styleSlot: "years",
		displaySlot: "yearsDisplay",
		unit: "years",
		numberFormatUnit: "year"
	},
	{
		valueField: "months",
		styleSlot: "months",
		displaySlot: "monthsDisplay",
		unit: "months",
		numberFormatUnit: "month"
	},
	{
		valueField: "weeks",
		styleSlot: "weeks",
		displaySlot: "weeksDisplay",
		unit: "weeks",
		numberFormatUnit: "week"
	},
	{
		valueField: "days",
		styleSlot: "days",
		displaySlot: "daysDisplay",
		unit: "days",
		numberFormatUnit: "day"
	},
	{
		valueField: "hours",
		styleSlot: "hours",
		displaySlot: "hoursDisplay",
		unit: "hours",
		numberFormatUnit: "hour"
	},
	{
		valueField: "minutes",
		styleSlot: "minutes",
		displaySlot: "minutesDisplay",
		unit: "minutes",
		numberFormatUnit: "minute"
	},
	{
		valueField: "seconds",
		styleSlot: "seconds",
		displaySlot: "secondsDisplay",
		unit: "seconds",
		numberFormatUnit: "second"
	},
	{
		valueField: "milliseconds",
		styleSlot: "milliseconds",
		displaySlot: "millisecondsDisplay",
		unit: "milliseconds",
		numberFormatUnit: "millisecond"
	},
	{
		valueField: "microseconds",
		styleSlot: "microseconds",
		displaySlot: "microsecondsDisplay",
		unit: "microseconds",
		numberFormatUnit: "microsecond"
	},
	{
		valueField: "nanoseconds",
		styleSlot: "nanoseconds",
		displaySlot: "nanosecondsDisplay",
		unit: "nanoseconds",
		numberFormatUnit: "nanosecond"
	}
];
//#endregion
//#region packages/intl-durationformat/get_internal_slots.ts
const internalSlotMap = /* @__PURE__ */ new WeakMap();
function getInternalSlots(x) {
	let internalSlots = internalSlotMap.get(x);
	if (!internalSlots) {
		internalSlots = Object.create(null);
		internalSlotMap.set(x, internalSlots);
	}
	return internalSlots;
}
//#endregion
//#region packages/intl-durationformat/abstract/PartitionDurationFormatPattern.ts
function PartitionDurationFormatPattern(df, duration) {
	const result = [];
	let done = false;
	let separated = false;
	const internalSlots = getInternalSlots(df);
	let dataLocale = internalSlots.dataLocale;
	const dataLocaleData = DurationFormat.localeData[dataLocale];
	if (!dataLocaleData) throw new TypeError("Invalid locale");
	const numberingSystem = internalSlots.numberingSystem;
	const separator = dataLocaleData.digitalFormat[numberingSystem];
	for (let i = 0; i < TABLE_2.length && !done; i++) {
		const row = TABLE_2[i];
		let value = new BigDecimal(duration[row.valueField]);
		const style = internalSlots[row.styleSlot];
		const display = internalSlots[row.displaySlot];
		const { unit, numberFormatUnit } = row;
		const nfOpts = Object.create(null);
		if (unit === "seconds" || unit === "milliseconds" || unit === "microseconds") {
			let nextStyle;
			if (unit === "seconds") nextStyle = internalSlots.milliseconds;
			else if (unit === "milliseconds") nextStyle = internalSlots.microseconds;
			else nextStyle = internalSlots.nanoseconds;
			if (nextStyle === "numeric" || nextStyle === "fractional") {
				if (unit === "seconds") value = value.plus(new BigDecimal(duration.milliseconds).div(1e3)).plus(new BigDecimal(duration.microseconds).div(1e6)).plus(new BigDecimal(duration.nanoseconds).div(1e9));
				else if (unit === "milliseconds") value = value.plus(new BigDecimal(duration.microseconds).div(1e3)).plus(new BigDecimal(duration.nanoseconds).div(1e6));
				else value = value.plus(new BigDecimal(duration.nanoseconds).div(1e3));
				if (internalSlots.fractionalDigits === void 0) {
					nfOpts.maximumFractionDigits = 9;
					nfOpts.minimumFractionDigits = 0;
				} else {
					nfOpts.maximumFractionDigits = internalSlots.fractionalDigits;
					nfOpts.minimumFractionDigits = internalSlots.fractionalDigits;
				}
				nfOpts.roundingMode = "trunc";
				done = true;
			}
		}
		if (!value.isZero() || display !== "auto") {
			nfOpts.numberingSystem = internalSlots.numberingSystem;
			if (style === "2-digit") nfOpts.minimumIntegerDigits = 2;
			if (style !== "2-digit" && style !== "numeric" && style !== "fractional") {
				nfOpts.style = "unit";
				nfOpts.unit = numberFormatUnit;
				nfOpts.unitDisplay = style;
			}
			const nf = createMemoizedNumberFormat(internalSlots.locale, nfOpts);
			let list;
			if (!separated) list = [];
			else {
				list = result[result.length - 1];
				list.push({
					type: "literal",
					value: separator
				});
			}
			nf.formatToParts(value).forEach(({ type, value }) => {
				list.push({
					type,
					value,
					unit: numberFormatUnit
				});
			});
			if (!separated) {
				if (style === "2-digit" || style === "numeric") separated = true;
				result.push(list);
			}
		} else separated = false;
	}
	const lfOpts = Object.create(null);
	lfOpts.type = "unit";
	let listStyle = internalSlots.style;
	if (listStyle === "digital") listStyle = "short";
	lfOpts.style = listStyle;
	const lf = createMemoizedListFormat(internalSlots.locale, lfOpts);
	const strings = [];
	for (const parts of result) {
		let string = "";
		for (const { value } of parts) string += value;
		strings.push(string);
	}
	let formatted = lf.formatToParts(strings);
	let resultIndex = 0;
	let resultLength = result.length;
	let flattened = [];
	for (const { type, value } of formatted) if (type === "element") {
		invariant$2(resultIndex < resultLength, "Index out of bounds");
		let parts = result[resultIndex];
		for (const part of parts) flattened.push(part);
		resultIndex++;
	} else {
		invariant$2(type === "literal", "Type must be literal");
		flattened.push({
			type: "literal",
			value
		});
	}
	return flattened;
}
//#endregion
//#region packages/ecma402-abstract/DurationFormat/constants.js
const TABLE_1 = [
	"years",
	"months",
	"weeks",
	"days",
	"hours",
	"minutes",
	"seconds",
	"milliseconds",
	"microseconds",
	"nanoseconds"
];
//#endregion
//#region packages/ecma402-abstract/DurationFormat/DurationRecordSign.js
function DurationRecordSign(record) {
	for (const key of TABLE_1) {
		if (record[key] < 0) return -1;
		if (record[key] > 0) return 1;
	}
	return 0;
}
//#endregion
//#region packages/ecma402-abstract/DurationFormat/IsValidDurationRecord.js
function IsValidDurationRecord(record) {
	const sign = DurationRecordSign(record);
	for (const key of TABLE_1) {
		const v = record[key];
		invariant$2(isFinite(Number(v)), `${key} is not finite`);
		if (v < 0 && sign > 0) return false;
		if (v > 0 && sign < 0) return false;
	}
	return true;
}
//#endregion
//#region packages/ecma262-abstract/ToPrimitive.js
function invariant$1(condition, message, Err = Error) {
	if (!condition) throw new Err(message);
}
function IsCallable(fn) {
	return typeof fn === "function";
}
function OrdinaryToPrimitive(O, hint) {
	let methodNames;
	if (hint === "string") methodNames = ["toString", "valueOf"];
	else methodNames = ["valueOf", "toString"];
	for (const name of methodNames) {
		const method = O[name];
		if (IsCallable(method)) {
			let result = method.call(O);
			if (typeof result !== "object") return result;
		}
	}
	throw new TypeError("Cannot convert object to primitive value");
}
/**
* https://tc39.es/ecma262/#sec-toprimitive
*/
function ToPrimitive(input, preferredType) {
	if (typeof input === "object" && input != null) {
		const exoticToPrim = Symbol.toPrimitive in input ? input[Symbol.toPrimitive] : void 0;
		let hint;
		if (exoticToPrim !== void 0) {
			if (preferredType === void 0) hint = "default";
			else if (preferredType === "string") hint = "string";
			else {
				invariant$1(preferredType === "number", "preferredType must be \"string\" or \"number\"");
				hint = "number";
			}
			let result = exoticToPrim.call(input, hint);
			if (typeof result !== "object") return result;
			throw new TypeError("Cannot convert exotic object to primitive.");
		}
		if (preferredType === void 0) preferredType = "number";
		return OrdinaryToPrimitive(input, preferredType);
	}
	return input;
}
//#endregion
//#region packages/ecma262-abstract/ToNumber.js
const ZERO = new Decimal(0);
function invariant(condition, message, Err = Error) {
	if (!condition) throw new Err(message);
}
/**
* https://tc39.es/ecma262/#sec-tonumber
*/
function ToNumber(arg) {
	if (typeof arg === "number") return new Decimal(arg);
	if (typeof arg === "bigint") return new Decimal(arg.toString());
	invariant(typeof arg !== "symbol", "Symbol is not supported", TypeError);
	if (arg === void 0) return new Decimal(NaN);
	if (arg === null || arg === 0) return ZERO;
	if (arg === true) return new Decimal(1);
	if (typeof arg === "string") try {
		return new Decimal(arg);
	} catch {
		return new Decimal(NaN);
	}
	invariant(typeof arg === "object", "object expected", TypeError);
	let primValue = ToPrimitive(arg, "number");
	invariant(typeof primValue !== "object", "object expected", TypeError);
	return ToNumber(primValue);
}
//#endregion
//#region packages/ecma402-abstract/DurationFormat/ToIntegerIfIntegral.js
function ToIntegerIfIntegral(arg) {
	const number = ToNumber(arg);
	invariant$2(number.isInteger(), `${arg} is not an integer`);
	return number.toNumber();
}
//#endregion
//#region packages/ecma402-abstract/DurationFormat/ToDurationRecord.js
function ToDurationRecord(input) {
	if (typeof input !== "object") {
		if (typeof input === "string") throw new RangeError("Invalid duration format");
		throw new TypeError("Invalid duration");
	}
	const result = {
		years: 0,
		months: 0,
		weeks: 0,
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
		milliseconds: 0,
		microseconds: 0,
		nanoseconds: 0
	};
	if (input.days !== void 0) result.days = ToIntegerIfIntegral(input.days);
	if (input.hours !== void 0) result.hours = ToIntegerIfIntegral(input.hours);
	if (input.microseconds !== void 0) result.microseconds = ToIntegerIfIntegral(input.microseconds);
	if (input.milliseconds !== void 0) result.milliseconds = ToIntegerIfIntegral(input.milliseconds);
	if (input.minutes !== void 0) result.minutes = ToIntegerIfIntegral(input.minutes);
	if (input.months !== void 0) result.months = ToIntegerIfIntegral(input.months);
	if (input.nanoseconds !== void 0) result.nanoseconds = ToIntegerIfIntegral(input.nanoseconds);
	if (input.seconds !== void 0) result.seconds = ToIntegerIfIntegral(input.seconds);
	if (input.weeks !== void 0) result.weeks = ToIntegerIfIntegral(input.weeks);
	if (input.years !== void 0) result.years = ToIntegerIfIntegral(input.years);
	if (input.years === void 0 && input.months === void 0 && input.weeks === void 0 && input.days === void 0 && input.hours === void 0 && input.minutes === void 0 && input.seconds === void 0 && input.milliseconds === void 0 && input.microseconds === void 0 && input.nanoseconds === void 0) throw new TypeError("Invalid duration format");
	if (!IsValidDurationRecord(result)) throw new RangeError("Invalid duration format");
	return result;
}
//#endregion
//#region node_modules/.aspect_rules_js/@formatjs_generated+cldr.number@0.0.0/node_modules/@formatjs_generated/cldr.number/numbering-systems.js
const numberingSystemNames = [
	"adlm",
	"ahom",
	"arab",
	"arabext",
	"armn",
	"armnlow",
	"bali",
	"beng",
	"bhks",
	"brah",
	"cakm",
	"cham",
	"cyrl",
	"deva",
	"diak",
	"ethi",
	"fullwide",
	"gara",
	"geor",
	"gong",
	"gonm",
	"grek",
	"greklow",
	"gujr",
	"gukh",
	"guru",
	"hanidays",
	"hanidec",
	"hans",
	"hansfin",
	"hant",
	"hantfin",
	"hebr",
	"hmng",
	"hmnp",
	"java",
	"jpan",
	"jpanfin",
	"jpanyear",
	"kali",
	"kawi",
	"khmr",
	"knda",
	"krai",
	"lana",
	"lanatham",
	"laoo",
	"latn",
	"lepc",
	"limb",
	"mathbold",
	"mathdbl",
	"mathmono",
	"mathsanb",
	"mathsans",
	"mlym",
	"modi",
	"mong",
	"mroo",
	"mtei",
	"mymr",
	"mymrepka",
	"mymrpao",
	"mymrshan",
	"mymrtlng",
	"nagm",
	"newa",
	"nkoo",
	"olck",
	"onao",
	"orya",
	"osma",
	"outlined",
	"rohg",
	"roman",
	"romanlow",
	"saur",
	"segment",
	"shrd",
	"sind",
	"sinh",
	"sora",
	"sund",
	"sunu",
	"takr",
	"talu",
	"taml",
	"tamldec",
	"telu",
	"thai",
	"tibt",
	"tirh",
	"tnsa",
	"tols",
	"vaii",
	"wara",
	"wcho"
];
//#endregion
//#region node_modules/.aspect_rules_js/@formatjs_generated+cldr.number@0.0.0/node_modules/@formatjs_generated/cldr.number/time-separators.js
const TIME_SEPARATORS = {
	"default": ":",
	"localeData": {
		"aa": { "nu": ["latn"] },
		"aa-DJ": { "nu": ["latn"] },
		"aa-ER": { "nu": ["latn"] },
		"ab": { "nu": ["latn"] },
		"af": { "nu": ["latn"] },
		"af-NA": { "nu": ["latn"] },
		"agq": { "nu": ["latn"] },
		"ak": { "nu": ["latn"] },
		"am": { "nu": ["latn"] },
		"an": { "nu": ["latn"] },
		"ann": { "nu": ["latn"] },
		"apc": { "nu": ["latn"] },
		"ar": { "nu": ["latn", "latn"] },
		"ar-AE": { "nu": ["latn", "latn"] },
		"ar-BH": { "nu": ["arab", "latn"] },
		"ar-DJ": { "nu": ["arab", "latn"] },
		"ar-DZ": { "nu": ["latn", "latn"] },
		"ar-EG": { "nu": ["arab", "latn"] },
		"ar-EH": { "nu": ["latn", "latn"] },
		"ar-ER": { "nu": ["arab", "latn"] },
		"ar-IL": { "nu": ["arab", "latn"] },
		"ar-IQ": { "nu": ["arab", "latn"] },
		"ar-JO": { "nu": ["arab", "latn"] },
		"ar-KM": { "nu": ["arab", "latn"] },
		"ar-KW": { "nu": ["arab", "latn"] },
		"ar-LB": { "nu": ["arab", "latn"] },
		"ar-LY": { "nu": ["latn", "latn"] },
		"ar-MA": { "nu": ["latn", "latn"] },
		"ar-MR": { "nu": ["arab", "latn"] },
		"ar-OM": { "nu": ["arab", "latn"] },
		"ar-PS": { "nu": ["arab", "latn"] },
		"ar-QA": { "nu": ["arab", "latn"] },
		"ar-SA": { "nu": ["arab", "latn"] },
		"ar-SD": { "nu": ["arab", "latn"] },
		"ar-SO": { "nu": ["arab", "latn"] },
		"ar-SS": { "nu": ["arab", "latn"] },
		"ar-SY": { "nu": ["arab", "latn"] },
		"ar-TD": { "nu": ["arab", "latn"] },
		"ar-TN": { "nu": ["latn", "latn"] },
		"ar-YE": { "nu": ["arab", "latn"] },
		"arn": { "nu": ["latn"] },
		"as": { "nu": ["beng"] },
		"asa": { "nu": ["latn"] },
		"ast": { "nu": ["latn"] },
		"az": { "nu": ["latn"] },
		"az-Arab": { "nu": ["arabext"] },
		"az-Arab-IQ": { "nu": ["arabext"] },
		"az-Arab-TR": { "nu": ["arabext"] },
		"az-Cyrl": { "nu": ["latn"] },
		"az-Latn": { "nu": ["latn"] },
		"ba": { "nu": ["latn"] },
		"bal": { "nu": ["latn"] },
		"bal-Arab": { "nu": ["latn"] },
		"bal-Latn": { "nu": ["latn"] },
		"bas": { "nu": ["latn"] },
		"be": { "nu": ["latn"] },
		"be-tarask": { "nu": ["latn"] },
		"bem": { "nu": ["latn"] },
		"bew": { "nu": ["latn"] },
		"bez": { "nu": ["latn"] },
		"bg": { "nu": ["latn"] },
		"bgc": { "nu": ["deva"] },
		"bgn": { "nu": ["arabext"] },
		"bgn-AE": { "nu": ["arabext"] },
		"bgn-AF": { "nu": ["arabext"] },
		"bgn-IR": { "nu": ["arabext"] },
		"bgn-OM": { "nu": ["arabext"] },
		"bho": { "nu": ["deva"] },
		"blo": { "nu": ["latn"] },
		"blt": { "nu": ["latn"] },
		"bm": { "nu": ["latn"] },
		"bm-Nkoo": { "nu": ["latn"] },
		"bn": { "nu": ["beng"] },
		"bn-IN": { "nu": ["beng"] },
		"bo": { "nu": ["latn"] },
		"bo-IN": { "nu": ["latn"] },
		"bqi": { "nu": ["latn"] },
		"br": { "nu": ["latn"] },
		"brx": { "nu": ["latn"] },
		"bs": { "nu": ["latn"] },
		"bs-Cyrl": { "nu": ["latn"] },
		"bs-Latn": { "nu": ["latn"] },
		"bss": { "nu": ["latn"] },
		"bua": { "nu": ["latn"] },
		"byn": { "nu": ["latn"] },
		"ca": { "nu": ["latn"] },
		"ca-AD": { "nu": ["latn"] },
		"ca-ES-valencia": { "nu": ["latn"] },
		"ca-FR": { "nu": ["latn"] },
		"ca-IT": { "nu": ["latn"] },
		"cad": { "nu": ["latn"] },
		"cch": { "nu": ["latn"] },
		"ccp": { "nu": ["cakm"] },
		"ccp-IN": { "nu": ["cakm"] },
		"ce": { "nu": ["latn"] },
		"ceb": { "nu": ["latn"] },
		"cgg": { "nu": ["latn"] },
		"cho": { "nu": ["latn"] },
		"chr": { "nu": ["latn"] },
		"cic": { "nu": ["latn"] },
		"ckb": { "nu": ["arab"] },
		"ckb-IR": { "nu": ["arab"] },
		"co": { "nu": ["latn"] },
		"cop": { "nu": ["latn"] },
		"cs": { "nu": ["latn"] },
		"csw": { "nu": ["latn"] },
		"cu": { "nu": ["latn"] },
		"cv": { "nu": ["latn"] },
		"cy": { "nu": ["latn"] },
		"da": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"da-GL": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"dav": { "nu": ["latn"] },
		"de": { "nu": ["latn"] },
		"de-AT": { "nu": ["latn"] },
		"de-BE": { "nu": ["latn"] },
		"de-CH": { "nu": ["latn"] },
		"de-IT": { "nu": ["latn"] },
		"de-LI": { "nu": ["latn"] },
		"de-LU": { "nu": ["latn"] },
		"dje": { "nu": ["latn"] },
		"doi": { "nu": ["latn"] },
		"dsb": { "nu": ["latn"] },
		"dua": { "nu": ["latn"] },
		"dv": { "nu": ["latn"] },
		"dyo": { "nu": ["latn"] },
		"dz": { "nu": ["tibt"] },
		"ebu": { "nu": ["latn"] },
		"ee": { "nu": ["latn"] },
		"ee-TG": { "nu": ["latn"] },
		"el": { "nu": ["latn"] },
		"el-CY": { "nu": ["latn"] },
		"el-polyton": { "nu": ["latn"] },
		"en": { "nu": ["latn"] },
		"en-001": { "nu": ["latn"] },
		"en-150": { "nu": ["latn"] },
		"en-AE": { "nu": ["latn"] },
		"en-AG": { "nu": ["latn"] },
		"en-AI": { "nu": ["latn"] },
		"en-AS": { "nu": ["latn"] },
		"en-AT": { "nu": ["latn"] },
		"en-AU": { "nu": ["latn"] },
		"en-BB": { "nu": ["latn"] },
		"en-BE": { "nu": ["latn"] },
		"en-BI": { "nu": ["latn"] },
		"en-BM": { "nu": ["latn"] },
		"en-BS": { "nu": ["latn"] },
		"en-BW": { "nu": ["latn"] },
		"en-BZ": { "nu": ["latn"] },
		"en-CA": { "nu": ["latn"] },
		"en-CC": { "nu": ["latn"] },
		"en-CH": { "nu": ["latn"] },
		"en-CK": { "nu": ["latn"] },
		"en-CM": { "nu": ["latn"] },
		"en-CX": { "nu": ["latn"] },
		"en-CY": { "nu": ["latn"] },
		"en-CZ": { "nu": ["latn"] },
		"en-DE": { "nu": ["latn"] },
		"en-DG": { "nu": ["latn"] },
		"en-DK": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"en-DM": { "nu": ["latn"] },
		"en-Dsrt": { "nu": ["latn"] },
		"en-EE": { "nu": ["latn"] },
		"en-ER": { "nu": ["latn"] },
		"en-ES": { "nu": ["latn"] },
		"en-FI": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"en-FJ": { "nu": ["latn"] },
		"en-FK": { "nu": ["latn"] },
		"en-FM": { "nu": ["latn"] },
		"en-FR": { "nu": ["latn"] },
		"en-GB": { "nu": ["latn"] },
		"en-GD": { "nu": ["latn"] },
		"en-GE": { "nu": ["latn"] },
		"en-GG": { "nu": ["latn"] },
		"en-GH": { "nu": ["latn"] },
		"en-GI": { "nu": ["latn"] },
		"en-GM": { "nu": ["latn"] },
		"en-GS": { "nu": ["latn"] },
		"en-GU": { "nu": ["latn"] },
		"en-GY": { "nu": ["latn"] },
		"en-HK": { "nu": ["latn"] },
		"en-HU": { "nu": ["latn"] },
		"en-ID": { "nu": ["latn"] },
		"en-IE": { "nu": ["latn"] },
		"en-IL": { "nu": ["latn"] },
		"en-IM": { "nu": ["latn"] },
		"en-IN": { "nu": ["latn"] },
		"en-IO": { "nu": ["latn"] },
		"en-IT": { "nu": ["latn"] },
		"en-JE": { "nu": ["latn"] },
		"en-JM": { "nu": ["latn"] },
		"en-JP": { "nu": ["latn"] },
		"en-KE": { "nu": ["latn"] },
		"en-KI": { "nu": ["latn"] },
		"en-KN": { "nu": ["latn"] },
		"en-KY": { "nu": ["latn"] },
		"en-LC": { "nu": ["latn"] },
		"en-LR": { "nu": ["latn"] },
		"en-LS": { "nu": ["latn"] },
		"en-LT": { "nu": ["latn"] },
		"en-LV": { "nu": ["latn"] },
		"en-MG": { "nu": ["latn"] },
		"en-MH": { "nu": ["latn"] },
		"en-MO": { "nu": ["latn"] },
		"en-MP": { "nu": ["latn"] },
		"en-MS": { "nu": ["latn"] },
		"en-MT": { "nu": ["latn"] },
		"en-MU": { "nu": ["latn"] },
		"en-MV": { "nu": ["latn"] },
		"en-MW": { "nu": ["latn"] },
		"en-MY": { "nu": ["latn"] },
		"en-NA": { "nu": ["latn"] },
		"en-NF": { "nu": ["latn"] },
		"en-NG": { "nu": ["latn"] },
		"en-NL": { "nu": ["latn"] },
		"en-NO": { "nu": ["latn"] },
		"en-NR": { "nu": ["latn"] },
		"en-NU": { "nu": ["latn"] },
		"en-NZ": { "nu": ["latn"] },
		"en-PG": { "nu": ["latn"] },
		"en-PH": { "nu": ["latn"] },
		"en-PK": { "nu": ["latn"] },
		"en-PL": { "nu": ["latn"] },
		"en-PN": { "nu": ["latn"] },
		"en-PR": { "nu": ["latn"] },
		"en-PT": { "nu": ["latn"] },
		"en-PW": { "nu": ["latn"] },
		"en-RO": { "nu": ["latn"] },
		"en-RW": { "nu": ["latn"] },
		"en-SB": { "nu": ["latn"] },
		"en-SC": { "nu": ["latn"] },
		"en-SD": { "nu": ["latn"] },
		"en-SE": { "nu": ["latn"] },
		"en-SG": { "nu": ["latn"] },
		"en-SH": { "nu": ["latn"] },
		"en-SI": { "nu": ["latn"] },
		"en-SK": { "nu": ["latn"] },
		"en-SL": { "nu": ["latn"] },
		"en-SS": { "nu": ["latn"] },
		"en-SX": { "nu": ["latn"] },
		"en-SZ": { "nu": ["latn"] },
		"en-Shaw": { "nu": ["latn"] },
		"en-TC": { "nu": ["latn"] },
		"en-TK": { "nu": ["latn"] },
		"en-TO": { "nu": ["latn"] },
		"en-TT": { "nu": ["latn"] },
		"en-TV": { "nu": ["latn"] },
		"en-TZ": { "nu": ["latn"] },
		"en-UA": { "nu": ["latn"] },
		"en-UG": { "nu": ["latn"] },
		"en-UM": { "nu": ["latn"] },
		"en-VC": { "nu": ["latn"] },
		"en-VG": { "nu": ["latn"] },
		"en-VI": { "nu": ["latn"] },
		"en-VU": { "nu": ["latn"] },
		"en-WS": { "nu": ["latn"] },
		"en-ZA": { "nu": ["latn"] },
		"en-ZM": { "nu": ["latn"] },
		"en-ZW": { "nu": ["latn"] },
		"eo": { "nu": ["latn"] },
		"es": { "nu": ["latn"] },
		"es-419": { "nu": ["latn"] },
		"es-AR": { "nu": ["latn"] },
		"es-BO": { "nu": ["latn"] },
		"es-BR": { "nu": ["latn"] },
		"es-BZ": { "nu": ["latn"] },
		"es-CL": { "nu": ["latn"] },
		"es-CO": { "nu": ["latn"] },
		"es-CR": { "nu": ["latn"] },
		"es-CU": { "nu": ["latn"] },
		"es-DO": { "nu": ["latn"] },
		"es-EA": { "nu": ["latn"] },
		"es-EC": { "nu": ["latn"] },
		"es-GQ": { "nu": ["latn"] },
		"es-GT": { "nu": ["latn"] },
		"es-HN": { "nu": ["latn"] },
		"es-IC": { "nu": ["latn"] },
		"es-MX": { "nu": ["latn"] },
		"es-NI": { "nu": ["latn"] },
		"es-PA": { "nu": ["latn"] },
		"es-PE": { "nu": ["latn"] },
		"es-PH": { "nu": ["latn"] },
		"es-PR": { "nu": ["latn"] },
		"es-PY": { "nu": ["latn"] },
		"es-SV": { "nu": ["latn"] },
		"es-US": { "nu": ["latn"] },
		"es-UY": { "nu": ["latn"] },
		"es-VE": { "nu": ["latn"] },
		"et": { "nu": ["latn"] },
		"eu": { "nu": ["latn"] },
		"ewo": { "nu": ["latn"] },
		"fa": { "nu": ["arabext"] },
		"fa-AF": { "nu": ["arabext"] },
		"ff": { "nu": ["latn"] },
		"ff-Adlm": { "nu": ["adlm"] },
		"ff-Adlm-BF": { "nu": ["adlm"] },
		"ff-Adlm-CM": { "nu": ["adlm"] },
		"ff-Adlm-GH": { "nu": ["adlm"] },
		"ff-Adlm-GM": { "nu": ["adlm"] },
		"ff-Adlm-GW": { "nu": ["adlm"] },
		"ff-Adlm-LR": { "nu": ["adlm"] },
		"ff-Adlm-MR": { "nu": ["adlm"] },
		"ff-Adlm-NE": { "nu": ["adlm"] },
		"ff-Adlm-NG": { "nu": ["adlm"] },
		"ff-Adlm-SL": { "nu": ["adlm"] },
		"ff-Adlm-SN": { "nu": ["adlm"] },
		"ff-Latn": { "nu": ["latn"] },
		"ff-Latn-BF": { "nu": ["latn"] },
		"ff-Latn-CM": { "nu": ["latn"] },
		"ff-Latn-GH": { "nu": ["latn"] },
		"ff-Latn-GM": { "nu": ["latn"] },
		"ff-Latn-GN": { "nu": ["latn"] },
		"ff-Latn-GW": { "nu": ["latn"] },
		"ff-Latn-LR": { "nu": ["latn"] },
		"ff-Latn-MR": { "nu": ["latn"] },
		"ff-Latn-NE": { "nu": ["latn"] },
		"ff-Latn-NG": { "nu": ["latn"] },
		"ff-Latn-SL": { "nu": ["latn"] },
		"fi": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"fil": { "nu": ["latn"] },
		"fo": { "nu": ["latn"] },
		"fo-DK": { "nu": ["latn"] },
		"fr": { "nu": ["latn"] },
		"fr-BE": { "nu": ["latn"] },
		"fr-BF": { "nu": ["latn"] },
		"fr-BI": { "nu": ["latn"] },
		"fr-BJ": { "nu": ["latn"] },
		"fr-BL": { "nu": ["latn"] },
		"fr-CA": { "nu": ["latn"] },
		"fr-CD": { "nu": ["latn"] },
		"fr-CF": { "nu": ["latn"] },
		"fr-CG": { "nu": ["latn"] },
		"fr-CH": { "nu": ["latn"] },
		"fr-CI": { "nu": ["latn"] },
		"fr-CM": { "nu": ["latn"] },
		"fr-DJ": { "nu": ["latn"] },
		"fr-DZ": { "nu": ["latn"] },
		"fr-GA": { "nu": ["latn"] },
		"fr-GF": { "nu": ["latn"] },
		"fr-GN": { "nu": ["latn"] },
		"fr-GP": { "nu": ["latn"] },
		"fr-GQ": { "nu": ["latn"] },
		"fr-HT": { "nu": ["latn"] },
		"fr-KM": { "nu": ["latn"] },
		"fr-LU": { "nu": ["latn"] },
		"fr-MA": { "nu": ["latn"] },
		"fr-MC": { "nu": ["latn"] },
		"fr-MF": { "nu": ["latn"] },
		"fr-MG": { "nu": ["latn"] },
		"fr-ML": { "nu": ["latn"] },
		"fr-MQ": { "nu": ["latn"] },
		"fr-MR": { "nu": ["latn"] },
		"fr-MU": { "nu": ["latn"] },
		"fr-NC": { "nu": ["latn"] },
		"fr-NE": { "nu": ["latn"] },
		"fr-PF": { "nu": ["latn"] },
		"fr-PM": { "nu": ["latn"] },
		"fr-RE": { "nu": ["latn"] },
		"fr-RW": { "nu": ["latn"] },
		"fr-SC": { "nu": ["latn"] },
		"fr-SN": { "nu": ["latn"] },
		"fr-SY": { "nu": ["latn"] },
		"fr-TD": { "nu": ["latn"] },
		"fr-TG": { "nu": ["latn"] },
		"fr-TN": { "nu": ["latn"] },
		"fr-VU": { "nu": ["latn"] },
		"fr-WF": { "nu": ["latn"] },
		"fr-YT": { "nu": ["latn"] },
		"frr": { "nu": ["latn"] },
		"fur": { "nu": ["latn"] },
		"fy": { "nu": ["latn"] },
		"ga": { "nu": ["latn"] },
		"ga-GB": { "nu": ["latn"] },
		"gaa": { "nu": ["latn"] },
		"gd": { "nu": ["latn"] },
		"gez": { "nu": ["latn"] },
		"gez-ER": { "nu": ["latn"] },
		"gl": { "nu": ["latn"] },
		"gn": { "nu": ["latn"] },
		"gsw": { "nu": ["latn"] },
		"gsw-FR": { "nu": ["latn"] },
		"gsw-LI": { "nu": ["latn"] },
		"gu": { "nu": ["latn"] },
		"guz": { "nu": ["latn"] },
		"gv": { "nu": ["latn"] },
		"ha": { "nu": ["latn"] },
		"ha-Arab": { "nu": ["latn"] },
		"ha-Arab-SD": { "nu": ["latn"] },
		"ha-GH": { "nu": ["latn"] },
		"ha-NE": { "nu": ["latn"] },
		"haw": { "nu": ["latn"] },
		"he": { "nu": ["latn"] },
		"hi": { "nu": ["latn"] },
		"hi-Latn": { "nu": ["latn"] },
		"hnj": { "nu": ["hmnp", "latn"] },
		"hnj-Hmnp": { "nu": ["hmnp", "latn"] },
		"hr": { "nu": ["latn"] },
		"hr-BA": { "nu": ["latn"] },
		"hsb": { "nu": ["latn"] },
		"ht": { "nu": ["latn"] },
		"hu": { "nu": ["latn"] },
		"hy": { "nu": ["latn"] },
		"ia": { "nu": ["latn"] },
		"id": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"ie": { "nu": ["latn"] },
		"ig": { "nu": ["latn"] },
		"ii": { "nu": ["latn"] },
		"io": { "nu": ["latn"] },
		"is": { "nu": ["latn"] },
		"it": { "nu": ["latn"] },
		"it-CH": { "nu": ["latn"] },
		"it-SM": { "nu": ["latn"] },
		"it-VA": { "nu": ["latn"] },
		"iu": { "nu": ["latn"] },
		"iu-Latn": { "nu": ["latn"] },
		"ja": { "nu": ["latn"] },
		"jbo": { "nu": ["latn"] },
		"jgo": { "nu": ["latn"] },
		"jmc": { "nu": ["latn"] },
		"jv": { "nu": ["latn"] },
		"ka": { "nu": ["latn"] },
		"kaa": { "nu": ["latn"] },
		"kaa-Cyrl": { "nu": ["latn"] },
		"kaa-Latn": { "nu": ["latn"] },
		"kab": { "nu": ["latn"] },
		"kaj": { "nu": ["latn"] },
		"kam": { "nu": ["latn"] },
		"kcg": { "nu": ["latn"] },
		"kde": { "nu": ["latn"] },
		"kea": { "nu": ["latn"] },
		"kek": { "nu": ["latn"] },
		"ken": { "nu": ["latn"] },
		"kgp": { "nu": ["latn"] },
		"khq": { "nu": ["latn"] },
		"ki": { "nu": ["latn"] },
		"kk": { "nu": ["latn"] },
		"kk-Arab": { "nu": ["latn"] },
		"kk-Cyrl": { "nu": ["latn"] },
		"kk-KZ": { "nu": ["latn"] },
		"kkj": { "nu": ["latn"] },
		"kl": { "nu": ["latn"] },
		"kln": { "nu": ["latn"] },
		"km": { "nu": ["latn"] },
		"kn": { "nu": ["latn"] },
		"ko": { "nu": ["latn"] },
		"ko-CN": { "nu": ["latn"] },
		"ko-KP": { "nu": ["latn"] },
		"kok": { "nu": ["latn"] },
		"kok-Deva": { "nu": ["latn"] },
		"kok-Latn": { "nu": ["latn"] },
		"kpe": { "nu": ["latn"] },
		"kpe-GN": { "nu": ["latn"] },
		"ks": { "nu": ["arabext"] },
		"ks-Arab": { "nu": ["arabext"] },
		"ks-Deva": { "nu": ["latn"] },
		"ksb": { "nu": ["latn"] },
		"ksf": { "nu": ["latn"] },
		"ksh": { "nu": ["latn"] },
		"ku": { "nu": ["latn"] },
		"ku-Arab": { "nu": ["latn"] },
		"ku-Arab-IR": { "nu": ["latn"] },
		"ku-Latn": { "nu": ["latn"] },
		"ku-Latn-IQ": { "nu": ["latn"] },
		"ku-Latn-SY": { "nu": ["latn"] },
		"ku-TR": { "nu": ["latn"] },
		"kw": { "nu": ["latn"] },
		"kxv": { "nu": ["latn"] },
		"kxv-Deva": { "nu": ["latn"] },
		"kxv-Latn": { "nu": ["latn"] },
		"kxv-Orya": { "nu": ["latn"] },
		"kxv-Telu": { "nu": ["latn"] },
		"ky": { "nu": ["latn"] },
		"la": { "nu": ["latn"] },
		"lag": { "nu": ["latn"] },
		"lb": { "nu": ["latn"] },
		"lg": { "nu": ["latn"] },
		"lij": { "nu": ["latn"] },
		"lkt": { "nu": ["latn"] },
		"lld": { "nu": ["latn"] },
		"lmo": { "nu": ["latn"] },
		"ln": { "nu": ["latn"] },
		"ln-AO": { "nu": ["latn"] },
		"ln-CF": { "nu": ["latn"] },
		"ln-CG": { "nu": ["latn"] },
		"lo": { "nu": ["latn"] },
		"lrc": { "nu": ["arabext"] },
		"lrc-IQ": { "nu": ["arabext"] },
		"lt": { "nu": ["latn"] },
		"ltg": { "nu": ["latn"] },
		"lu": { "nu": ["latn"] },
		"luo": { "nu": ["latn"] },
		"luy": { "nu": ["latn"] },
		"lv": { "nu": ["latn"] },
		"lzz": { "nu": ["latn"] },
		"mai": { "nu": ["latn"] },
		"mas": { "nu": ["latn"] },
		"mas-TZ": { "nu": ["latn"] },
		"mdf": { "nu": ["latn"] },
		"mer": { "nu": ["latn"] },
		"mfe": { "nu": ["latn"] },
		"mg": { "nu": ["latn"] },
		"mgh": { "nu": ["latn"] },
		"mgo": { "nu": ["latn"] },
		"mhn": { "nu": ["latn"] },
		"mi": { "nu": ["latn"] },
		"mic": { "nu": ["latn"] },
		"mk": { "nu": ["latn"] },
		"ml": { "nu": ["latn"] },
		"mn": { "nu": ["latn"] },
		"mn-Mong": { "nu": ["latn"] },
		"mn-Mong-MN": { "nu": ["latn"] },
		"mni": { "nu": ["beng"] },
		"mni-Beng": { "nu": ["beng"] },
		"mni-Mtei": { "nu": ["mtei"] },
		"moh": { "nu": ["latn"] },
		"mr": { "nu": ["deva"] },
		"ms": { "nu": ["latn"] },
		"ms-Arab": { "nu": ["latn"] },
		"ms-Arab-BN": { "nu": ["latn"] },
		"ms-BN": { "nu": ["latn"] },
		"ms-ID": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"ms-SG": { "nu": ["latn"] },
		"mt": { "nu": ["latn"] },
		"mua": { "nu": ["latn"] },
		"mus": { "nu": ["latn"] },
		"mww": { "nu": ["hmnp", "latn"] },
		"mww-Hmnp": { "nu": ["hmnp", "latn"] },
		"my": { "nu": ["mymr"] },
		"myv": { "nu": ["latn"] },
		"mzn": { "nu": ["arabext"] },
		"naq": { "nu": ["latn"] },
		"nb": { "nu": ["latn"] },
		"nb-SJ": { "nu": ["latn"] },
		"nd": { "nu": ["latn"] },
		"nds": { "nu": ["latn"] },
		"nds-NL": { "nu": ["latn"] },
		"ne": { "nu": ["deva"] },
		"ne-IN": { "nu": ["deva"] },
		"nl": { "nu": ["latn"] },
		"nl-AW": { "nu": ["latn"] },
		"nl-BE": { "nu": ["latn"] },
		"nl-BQ": { "nu": ["latn"] },
		"nl-CW": { "nu": ["latn"] },
		"nl-SR": { "nu": ["latn"] },
		"nl-SX": { "nu": ["latn"] },
		"nmg": { "nu": ["latn"] },
		"nn": { "nu": ["latn"] },
		"nnh": { "nu": ["latn"] },
		"no": { "nu": ["latn"] },
		"nqo": { "nu": ["nkoo"] },
		"nr": { "nu": ["latn"] },
		"nso": { "nu": ["latn"] },
		"nus": { "nu": ["latn"] },
		"nv": { "nu": ["latn"] },
		"ny": { "nu": ["latn"] },
		"nyn": { "nu": ["latn"] },
		"oc": { "nu": ["latn"] },
		"oc-ES": { "nu": ["latn"] },
		"oka": { "nu": ["latn"] },
		"oka-US": { "nu": ["latn"] },
		"om": { "nu": ["latn"] },
		"om-KE": { "nu": ["latn"] },
		"or": { "nu": ["latn"] },
		"os": { "nu": ["latn"] },
		"os-RU": { "nu": ["latn"] },
		"osa": { "nu": ["latn"] },
		"pa": { "nu": ["latn"] },
		"pa-Arab": { "nu": ["arabext"] },
		"pa-Guru": { "nu": ["latn"] },
		"pap": { "nu": ["latn"] },
		"pap-AW": { "nu": ["latn"] },
		"pcm": { "nu": ["latn"] },
		"pi": { "nu": ["latn"] },
		"pi-Latn": { "nu": ["latn"] },
		"pis": { "nu": ["latn"] },
		"pl": { "nu": ["latn"] },
		"pms": { "nu": ["latn"] },
		"prg": { "nu": ["latn"] },
		"ps": { "nu": ["arabext"] },
		"ps-PK": { "nu": ["arabext"] },
		"pt": { "nu": ["latn"] },
		"pt-AO": { "nu": ["latn"] },
		"pt-CH": { "nu": ["latn"] },
		"pt-CV": { "nu": ["latn"] },
		"pt-GQ": { "nu": ["latn"] },
		"pt-GW": { "nu": ["latn"] },
		"pt-LU": { "nu": ["latn"] },
		"pt-MO": { "nu": ["latn"] },
		"pt-MZ": { "nu": ["latn"] },
		"pt-PT": { "nu": ["latn"] },
		"pt-ST": { "nu": ["latn"] },
		"pt-TL": { "nu": ["latn"] },
		"qu": { "nu": ["latn"] },
		"qu-BO": { "nu": ["latn"] },
		"qu-EC": { "nu": ["latn"] },
		"quc": { "nu": ["latn"] },
		"raj": { "nu": ["deva"] },
		"rhg": { "nu": ["latn"] },
		"rhg-Rohg": { "nu": ["latn"] },
		"rhg-Rohg-BD": { "nu": ["latn"] },
		"rif": { "nu": ["latn"] },
		"rm": { "nu": ["latn"] },
		"rn": { "nu": ["latn"] },
		"ro": { "nu": ["latn"] },
		"ro-MD": { "nu": ["latn"] },
		"rof": { "nu": ["latn"] },
		"ru": { "nu": ["latn"] },
		"ru-BY": { "nu": ["latn"] },
		"ru-KG": { "nu": ["latn"] },
		"ru-KZ": { "nu": ["latn"] },
		"ru-MD": { "nu": ["latn"] },
		"ru-UA": { "nu": ["latn"] },
		"rw": { "nu": ["latn"] },
		"rwk": { "nu": ["latn"] },
		"sa": { "nu": ["deva"] },
		"sah": { "nu": ["latn"] },
		"saq": { "nu": ["latn"] },
		"sat": { "nu": ["olck"] },
		"sat-Deva": { "nu": ["deva"] },
		"sat-Olck": { "nu": ["olck"] },
		"sbp": { "nu": ["latn"] },
		"sc": { "nu": ["latn"] },
		"scn": { "nu": ["latn"] },
		"sd": { "nu": ["arab"] },
		"sd-Arab": { "nu": ["arab"] },
		"sd-Deva": { "nu": ["latn"] },
		"sdh": { "nu": ["arab"] },
		"sdh-IQ": { "nu": ["arab"] },
		"se": { "nu": ["latn"] },
		"se-FI": { "nu": ["latn"] },
		"se-SE": { "nu": ["latn"] },
		"seh": { "nu": ["latn"] },
		"ses": { "nu": ["latn"] },
		"sg": { "nu": ["latn"] },
		"sgs": { "nu": ["latn"] },
		"shi": { "nu": ["latn"] },
		"shi-Latn": { "nu": ["latn"] },
		"shi-Tfng": { "nu": ["latn"] },
		"shn": { "nu": ["latn"] },
		"shn-TH": { "nu": ["latn"] },
		"si": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"sid": { "nu": ["latn"] },
		"sk": { "nu": ["latn"] },
		"skr": { "nu": ["latn"] },
		"sl": { "nu": ["latn"] },
		"sma": { "nu": ["latn"] },
		"sma-NO": { "nu": ["latn"] },
		"smj": { "nu": ["latn"] },
		"smj-NO": { "nu": ["latn"] },
		"smn": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"sms": { "nu": ["latn"] },
		"sn": { "nu": ["latn"] },
		"so": { "nu": ["latn"] },
		"so-DJ": { "nu": ["latn"] },
		"so-ET": { "nu": ["latn"] },
		"so-KE": { "nu": ["latn"] },
		"sq": { "nu": ["latn"] },
		"sq-MK": { "nu": ["latn"] },
		"sq-XK": { "nu": ["latn"] },
		"sr": { "nu": ["latn"] },
		"sr-Cyrl": { "nu": ["latn"] },
		"sr-Cyrl-BA": { "nu": ["latn"] },
		"sr-Cyrl-ME": { "nu": ["latn"] },
		"sr-Cyrl-XK": { "nu": ["latn"] },
		"sr-Latn": { "nu": ["latn"] },
		"sr-Latn-BA": { "nu": ["latn"] },
		"sr-Latn-ME": { "nu": ["latn"] },
		"sr-Latn-XK": { "nu": ["latn"] },
		"ss": { "nu": ["latn"] },
		"ss-SZ": { "nu": ["latn"] },
		"ssy": { "nu": ["latn"] },
		"st": { "nu": ["latn"] },
		"st-LS": { "nu": ["latn"] },
		"su": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"su-Latn": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"suz": { "nu": ["latn"] },
		"suz-Deva": { "nu": ["latn"] },
		"suz-Sunu": { "nu": ["latn"] },
		"sv": { "nu": ["latn"] },
		"sv-AX": { "nu": ["latn"] },
		"sv-FI": {
			"nu": ["latn"],
			"separator": { "latn": "." }
		},
		"sw": { "nu": ["latn"] },
		"sw-CD": { "nu": ["latn"] },
		"sw-KE": { "nu": ["latn"] },
		"sw-UG": { "nu": ["latn"] },
		"syr": { "nu": ["latn"] },
		"syr-SY": { "nu": ["latn"] },
		"szl": { "nu": ["latn"] },
		"ta": { "nu": ["latn"] },
		"ta-LK": { "nu": ["latn"] },
		"ta-MY": { "nu": ["latn"] },
		"ta-SG": { "nu": ["latn"] },
		"te": { "nu": ["latn"] },
		"teo": { "nu": ["latn"] },
		"teo-KE": { "nu": ["latn"] },
		"tg": { "nu": ["latn"] },
		"th": { "nu": ["latn"] },
		"ti": { "nu": ["latn"] },
		"ti-ER": { "nu": ["latn"] },
		"tig": { "nu": ["latn"] },
		"tk": { "nu": ["latn"] },
		"tn": { "nu": ["latn"] },
		"tn-BW": { "nu": ["latn"] },
		"to": { "nu": ["latn"] },
		"tok": { "nu": ["latn"] },
		"tpi": { "nu": ["latn"] },
		"tr": { "nu": ["latn"] },
		"tr-CY": { "nu": ["latn"] },
		"trv": { "nu": ["latn"] },
		"trw": { "nu": ["latn"] },
		"ts": { "nu": ["latn"] },
		"tt": { "nu": ["latn"] },
		"twq": { "nu": ["latn"] },
		"tyv": { "nu": ["latn"] },
		"tzm": { "nu": ["latn"] },
		"ug": { "nu": ["latn"] },
		"uk": { "nu": ["latn"] },
		"und": { "nu": ["latn"] },
		"ur": { "nu": ["latn"] },
		"ur-IN": {
			"nu": ["arabext"],
			"separator": { "arabext": "٫" }
		},
		"uz": { "nu": ["latn"] },
		"uz-Arab": { "nu": ["arabext"] },
		"uz-Cyrl": { "nu": ["latn"] },
		"uz-Latn": { "nu": ["latn"] },
		"vai": { "nu": ["latn"] },
		"vai-Latn": { "nu": ["latn"] },
		"vai-Vaii": { "nu": ["latn"] },
		"ve": { "nu": ["latn"] },
		"vec": { "nu": ["latn"] },
		"vi": { "nu": ["latn"] },
		"vmw": { "nu": ["latn"] },
		"vo": { "nu": ["latn"] },
		"vun": { "nu": ["latn"] },
		"wa": { "nu": ["latn"] },
		"wae": { "nu": ["latn"] },
		"wal": { "nu": ["latn"] },
		"wbp": { "nu": ["latn"] },
		"wo": { "nu": ["latn"] },
		"xh": { "nu": ["latn"] },
		"xnr": { "nu": ["latn"] },
		"xog": { "nu": ["latn"] },
		"yav": { "nu": ["latn"] },
		"yi": { "nu": ["latn"] },
		"yo": { "nu": ["latn"] },
		"yo-BJ": { "nu": ["latn"] },
		"yrl": { "nu": ["latn"] },
		"yrl-CO": { "nu": ["latn"] },
		"yrl-VE": { "nu": ["latn"] },
		"yue": { "nu": ["latn"] },
		"yue-Hans": { "nu": ["latn"] },
		"yue-Hant": { "nu": ["latn"] },
		"yue-Hant-CN": { "nu": ["latn"] },
		"yue-Hant-MO": { "nu": ["latn"] },
		"za": { "nu": ["latn"] },
		"zgh": { "nu": ["latn"] },
		"zh": { "nu": ["latn"] },
		"zh-Hans": { "nu": ["latn"] },
		"zh-Hans-HK": { "nu": ["latn"] },
		"zh-Hans-MO": { "nu": ["latn"] },
		"zh-Hans-MY": { "nu": ["latn"] },
		"zh-Hans-SG": { "nu": ["latn"] },
		"zh-Hant": { "nu": ["latn"] },
		"zh-Hant-HK": { "nu": ["latn"] },
		"zh-Hant-MO": { "nu": ["latn"] },
		"zh-Hant-MY": { "nu": ["latn"] },
		"zh-Latn": { "nu": ["latn"] },
		"zu": { "nu": ["latn"] }
	}
};
//#endregion
//#region packages/intl-durationformat/core.ts
const RESOLVED_OPTIONS_KEYS = [
	"locale",
	"style",
	"years",
	"yearsDisplay",
	"months",
	"monthsDisplay",
	"weeks",
	"weeksDisplay",
	"days",
	"daysDisplay",
	"hours",
	"hoursDisplay",
	"minutes",
	"minutesDisplay",
	"seconds",
	"secondsDisplay",
	"milliseconds",
	"millisecondsDisplay",
	"microseconds",
	"microsecondsDisplay",
	"nanoseconds",
	"nanosecondsDisplay",
	"numberingSystem",
	"fractionalDigits"
];
const TABLE_3 = [
	{
		styleSlot: "years",
		displaySlot: "yearsDisplay",
		unit: "years",
		values: [
			"long",
			"short",
			"narrow"
		],
		digitalDefault: "short"
	},
	{
		styleSlot: "months",
		displaySlot: "monthsDisplay",
		unit: "months",
		values: [
			"long",
			"short",
			"narrow"
		],
		digitalDefault: "short"
	},
	{
		styleSlot: "weeks",
		displaySlot: "weeksDisplay",
		unit: "weeks",
		values: [
			"long",
			"short",
			"narrow"
		],
		digitalDefault: "short"
	},
	{
		styleSlot: "days",
		displaySlot: "daysDisplay",
		unit: "days",
		values: [
			"long",
			"short",
			"narrow"
		],
		digitalDefault: "short"
	},
	{
		styleSlot: "hours",
		displaySlot: "hoursDisplay",
		unit: "hours",
		values: [
			"long",
			"short",
			"narrow",
			"numeric",
			"2-digit"
		],
		digitalDefault: "numeric"
	},
	{
		styleSlot: "minutes",
		displaySlot: "minutesDisplay",
		unit: "minutes",
		values: [
			"long",
			"short",
			"narrow",
			"numeric",
			"2-digit"
		],
		digitalDefault: "numeric"
	},
	{
		styleSlot: "seconds",
		displaySlot: "secondsDisplay",
		unit: "seconds",
		values: [
			"long",
			"short",
			"narrow",
			"numeric",
			"2-digit"
		],
		digitalDefault: "numeric"
	},
	{
		styleSlot: "milliseconds",
		displaySlot: "millisecondsDisplay",
		unit: "milliseconds",
		values: [
			"long",
			"short",
			"narrow",
			"numeric"
		],
		digitalDefault: "numeric"
	},
	{
		styleSlot: "microseconds",
		displaySlot: "microsecondsDisplay",
		unit: "microseconds",
		values: [
			"long",
			"short",
			"narrow",
			"numeric"
		],
		digitalDefault: "numeric"
	},
	{
		styleSlot: "nanoseconds",
		displaySlot: "nanosecondsDisplay",
		unit: "nanoseconds",
		values: [
			"long",
			"short",
			"narrow",
			"numeric"
		],
		digitalDefault: "numeric"
	}
];
/**
* DurationFormat provides locale-sensitive duration formatting
* Implements the Intl.DurationFormat API from the TC39 proposal
*/
var DurationFormat = class DurationFormat {
	constructor(locales, options) {
		if (!(this && this instanceof DurationFormat ? this.constructor : void 0)) throw new TypeError("Intl.DurationFormat must be called with 'new'");
		const requestedLocales = CanonicalizeLocaleList(locales);
		const opt = Object.create(null);
		const opts = options === void 0 ? Object.create(null) : ToObject(options);
		const matcher = GetOption(opts, "localeMatcher", "string", ["best fit", "lookup"], "best fit");
		const numberingSystem = GetOption(opts, "numberingSystem", "string", void 0, void 0);
		if (numberingSystem !== void 0 && numberingSystemNames.indexOf(numberingSystem) < 0) throw RangeError(`Invalid numberingSystems: ${numberingSystem}`);
		opt.nu = numberingSystem;
		opt.localeMatcher = matcher;
		const { localeData, availableLocales } = DurationFormat;
		const r = ResolveLocale(availableLocales, requestedLocales, opt, ["nu"], localeData, DurationFormat.getDefaultLocale);
		const locale = r.locale;
		const internalSlots = getInternalSlots(this);
		internalSlots.initializedDurationFormat = true;
		internalSlots.locale = locale;
		internalSlots.numberingSystem = r.nu;
		const style = GetOption(opts, "style", "string", [
			"long",
			"short",
			"narrow",
			"digital"
		], "short");
		internalSlots.style = style;
		internalSlots.dataLocale = r.dataLocale;
		let prevStyle = "";
		TABLE_3.forEach((row) => {
			const { styleSlot, displaySlot, unit, values: valueList, digitalDefault: digitalBase } = row;
			const unitOptions = GetDurationUnitOptions(unit, opts, style, valueList, digitalBase, prevStyle);
			internalSlots[styleSlot] = unitOptions.style;
			internalSlots[displaySlot] = unitOptions.display;
			if (unit === "hours" || unit === "minutes" || unit === "seconds" || unit === "milliseconds" || unit === "microseconds") prevStyle = unitOptions.style;
		});
		internalSlots.fractionalDigits = GetNumberOption(opts, "fractionalDigits", 0, 9, void 0);
	}
	resolvedOptions() {
		if (typeof this !== "object" || !OrdinaryHasInstance(DurationFormat, this)) throw TypeError("Method Intl.DurationFormat.prototype.resolvedOptions called on incompatible receiver");
		const internalSlots = getInternalSlots(this);
		const ro = {};
		for (const key of RESOLVED_OPTIONS_KEYS) {
			let v = internalSlots[key];
			if (key === "fractionalDigits") {
				if (v !== void 0) v = Number(v);
			} else if (v === "fractional") v = "numeric";
			else invariant$2(v !== void 0, `Missing internal slot ${key}`);
			ro[key] = v;
		}
		return ro;
	}
	formatToParts(duration) {
		if (getInternalSlots(this).initializedDurationFormat === void 0) throw new TypeError("Error uninitialized locale");
		const record = ToDurationRecord(duration);
		const parts = PartitionDurationFormatPattern(this, record);
		const result = [];
		for (const { type, unit, value } of parts) {
			const obj = {
				type,
				value
			};
			if (unit) obj.unit = unit;
			result.push(obj);
		}
		return result;
	}
	format(duration) {
		if (getInternalSlots(this).initializedDurationFormat === void 0) throw new TypeError("Error uninitialized locale");
		const record = ToDurationRecord(duration);
		const parts = PartitionDurationFormatPattern(this, record);
		let result = "";
		for (const { value } of parts) result += value;
		return result;
	}
	static supportedLocalesOf(locales, options) {
		return SupportedLocales(DurationFormat.availableLocales, CanonicalizeLocaleList(locales), options);
	}
	static {
		this.__defaultLocale = "en";
	}
	static {
		this.availableLocales = /* @__PURE__ */ new Set();
	}
	static {
		this.localeData = Object.keys(TIME_SEPARATORS.localeData).reduce((all, locale) => {
			DurationFormat.availableLocales.add(locale);
			const nu = TIME_SEPARATORS.localeData[locale].nu;
			all[locale] = {
				nu,
				digitalFormat: TIME_SEPARATORS.localeData[locale].separator || nu.reduce((separators, n) => {
					separators[n] = TIME_SEPARATORS.default;
					return separators;
				}, {})
			};
			return all;
		}, {});
	}
	static {
		this.getDefaultLocale = () => {
			return DurationFormat.__defaultLocale;
		};
	}
	static {
		this.polyfilled = true;
	}
};
//#endregion
//#region packages/intl-durationformat/polyfill-force.ts
defineProperty(ensureIntl(), "DurationFormat", { value: DurationFormat });
//#endregion

//# sourceMappingURL=polyfill-force.js.map